import {
  createSignal,
  Show,
  type Component,
  For,
  onMount,
  createEffect,
  createMemo,
} from 'solid-js';
import { RgbColor, HsvColor, HuePicker } from 'solid-color';
import { t } from '@trimble/i18n';
import tinycolor from 'tinycolor2';
import { Saturation } from './Saturation';
import { ColorInputField } from './ColorInputField';
import { defaultColorsFlat } from './default-color-palette';
import { appendToPalette, getPaletteFromStorage } from './custom-color-storage';
import { ModusWcSliderCustomEvent } from '@trimble-oss/moduswebcomponents';
import { colorsAreEqual } from '@trimble/utilities';
import { getClosestNamedColor } from './color-utils';

export interface ColorPickerPanelProps {
  initialColor?: string;
  useTransparency?: boolean;
  initialTransparency?: number;
  /**
   * Optional ByLayer functionality
   */
  byLayerColor?: string | null;
  /**
   * Object's specific color (null means ByLayer)
   */
  objectColor?: string | null;
  /**
   * When true, do not visually mark any palette color as selected
   */
  suppressSelection?: boolean;
  /**
   * Called when the transparency slider value is changed
   * @param transparency Normalized transparency value (0.0 - 1.0 inc)
   */
  onTransparencyChange?(transparency: number): void;
  onByLayerSelect?(): void;
  onChange?(rgb: RgbColor): void;
  onSave?(rgb: RgbColor): void;
  onReset?(): void;
  /**
   * When true, all controls are disabled
   */
  disabled?: boolean;
  /**
   * When true, removes the container styling (background, shadow, padding) for use in popups
   */
  noContainer?: boolean;
  /**
   * When true, shows the reset button. Defaults to false.
   */
  showResetButton?: boolean;
}

/* eslint-disable @typescript-eslint/naming-convention */
const customSliderPointerStyle = {
  width: '20px',
  height: '20px',
  transform: 'translate(-10px, -5px)',
  'border-radius': '50%',
  'background-color': 'rgb(248, 248, 248)',
  'box-shadow': '0 1px 4px 0 rgba(0, 0, 0, 0.37)',
};
/* eslint-enable @typescript-eslint/naming-convention */

const transparencySliderRange = {
  min: 0,
  max: 255,
  step: 1,
};

export const ColorPickerPanel: Component<ColorPickerPanelProps> = (props) => {
  const [showColorSelector, setShowColorSelector] =
    createSignal<boolean>(false);
  const [rgbState, setRgbState] = createSignal<RgbColor>({ r: 0, g: 0, b: 0 });
  const [hsvState, setHsvState] = createSignal<HsvColor>({ h: 0, s: 0, v: 0 });

  const [customColors, setCustomColors] = createSignal<RgbColor[]>([]);
  const [transparency, setTransparency] = createSignal<number>(0.0);

  // Create a computed signal that includes object color in custom colors if needed
  const allCustomColors = createMemo(() => {
    const colors = [...customColors()];

    // Add object color if it exists and is not in the default palette
    if (props.objectColor) {
      const objectColorRgb = tinycolor(props.objectColor).toRgb();
      const objectColorHex = tinycolor(props.objectColor).toHex();

      // Check if object color is already in default colors
      const isInDefaultColors = defaultColorsFlat.some((defaultColor) =>
        colorsAreEqual(defaultColor, objectColorHex)
      );

      // Check if object color is already in custom colors
      const isInCustomColors = colors.some((customColor) =>
        colorsAreEqual(tinycolor(customColor).toHex(), objectColorHex)
      );

      // Add object color if it's not in either default or custom colors
      if (!isInDefaultColors && !isInCustomColors) {
        colors.unshift(objectColorRgb); // Add at the beginning
      }
    }

    return colors;
  });

  onMount(() => {
    let initialColor = props.initialColor;

    // If object has a specific color (not null), use that instead of initialColor
    if (props.objectColor) {
      initialColor = props.objectColor;
    }

    if (initialColor) {
      const color = tinycolor(initialColor);
      if (color.isValid()) {
        setRgbState(color.toRgb());
        setHsvState(color.toHsv());
      }
    }
    setTransparency(props.initialTransparency ?? 0.0);

    // Add the initial color if its not part of the palette
    const states: RgbColor[] = [];
    const colors = getPaletteFromStorage();

    if (
      initialColor &&
      !defaultColorsFlat.some((x) => colorsAreEqual(initialColor, x)) &&
      !colors.some((x) => colorsAreEqual(initialColor, x))
    ) {
      const customColor = tinycolor(initialColor);

      if (customColor.isValid()) {
        states.push(customColor.toRgb());
      }
    }

    if (colors.length > 0) {
      colors.forEach((color) => {
        const currentColor = tinycolor(color);
        states.push(currentColor.toRgb());
      });
    }

    if (states.length > 0) {
      setCustomColors([...customColors(), ...states]);
    }
  });

  // Watch for changes in initialColor or objectColor props and update state
  createEffect(() => {
    // Determine the color to display
    let colorToUse = props.initialColor;

    // If object has a specific color (not null), use that instead of initialColor
    if (props.objectColor) {
      colorToUse = props.objectColor;
    }

    if (colorToUse) {
      const color = tinycolor(colorToUse);
      if (color.isValid()) {
        setRgbState(color.toRgb());
        setHsvState(color.toHsv());
      }
    }
  });

  // Watch for changes in initialTransparency prop and update state
  createEffect(() => {
    if (props.initialTransparency !== undefined) {
      setTransparency(props.initialTransparency);
    }
  });

  function handleColorChange(rgb?: RgbColor, hsv?: HsvColor): void {
    if (props.disabled) return;

    if (rgb) {
      setRgbState(rgb);
      const newHsv = tinycolor(rgb).toHsv();
      // preserve the previous hue if color is grey or black
      // otherwise hue slider will reset to far left as there is no color (hue is 0)
      if (newHsv.s === 0) {
        newHsv.h = hsvState().h;
      }
      setHsvState(newHsv);
      props.onChange?.(rgb);
    }
    if (hsv) {
      setHsvState(hsv);
      const hsvToRgb = tinycolor(hsv).toRgb();
      setRgbState(hsvToRgb);
      props.onChange?.(hsvToRgb);
    }
  }

  function onEditableRChange(r: string): void {
    if (props.disabled) return;
    const rNum = Number.parseInt(r, 10);
    handleColorChange(
      { r: +rNum, g: rgbState().g, b: rgbState().b },
      undefined
    );
  }

  function onEditableGChange(g: string): void {
    if (props.disabled) return;
    const gNum = Number.parseInt(g, 10);
    handleColorChange(
      { r: rgbState().r, g: +gNum, b: rgbState().b },
      undefined
    );
  }

  function onEditableBChange(b: string): void {
    if (props.disabled) return;
    const bNum = Number.parseInt(b, 10);
    handleColorChange(
      { r: rgbState().r, g: rgbState().g, b: +bNum },
      undefined
    );
  }

  function onTransparencyChange(value: string): void {
    if (props.disabled) return;

    const transparencyParsed: number = Number.parseInt(value, 10);
    if (isNaN(transparencyParsed)) {
      return;
    }
    const transparencyNormalized: number =
      1 -
      (transparencyParsed - transparencySliderRange.min) /
        (transparencySliderRange.max - transparencySliderRange.min);
    setTransparency(transparencyNormalized);
    props.onTransparencyChange?.(transparencyNormalized);
  }

  function onCustomColorCreated(): void {
    if (props.disabled) return;
    setCustomColors([...customColors(), rgbState()]);
    appendToPalette(tinycolor(rgbState()).toHexString());
    setShowColorSelector(false);
    props.onSave?.(rgbState());
  }

  /**
   * Determines if a color should be selected, considering object color priority
   */
  function shouldSelectColor(
    paletteColor: string,
    isByLayer: boolean = false
  ): boolean {
    if (props.suppressSelection) {
      return false;
    }
    const currentColorHex = tinycolor(rgbState()).toHexString();
    const isColorMatch = colorsAreEqual(paletteColor, currentColorHex);

    if (!isColorMatch) {
      return false;
    }

    // If ByLayer is active (objectColor is null), only select the ByLayer option
    if (props.objectColor === null) {
      return isByLayer; // Only select if it IS the ByLayer color
    }

    // If object color exists and matches, only select the object color (not ByLayer)
    if (
      props.objectColor &&
      colorsAreEqual(props.objectColor, currentColorHex)
    ) {
      return !isByLayer; // Only select if it's NOT the ByLayer color
    }

    // If no object color or object color doesn't match, use normal selection
    return true;
  }

  /**
   * Gets additional CSS style classes if the color palette is selected/applied
   */
  function getSelectedClass(
    paletteColor: string,
    isByLayer: boolean = false
  ): string {
    return shouldSelectColor(paletteColor, isByLayer)
      ? 'ring-gray-3 ring-[5px]'
      : '';
  }

  return (
    <div
      data-testid="color-picker-panel-container"
      class={`flex flex-col gap-3 w-[260px] ${
        props.noContainer ? '' : 'bg-white shadow-md shadow-gray-8 p-2'
      } ${props.disabled ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <label class="text-xs text-gray-6">{t('general.color')}</label>
      <Show when={!showColorSelector()}>
        <div
          data-testid="palette-container"
          class={`grid grid-cols-6 grid-flow-row gap-[11px] ${
            props.byLayerColor ? 'grid-rows-6' : 'grid-rows-5'
          }`}
        >
          {/* ByLayer option - only show if enabled */}
          <Show when={props.byLayerColor}>
            <div
              class={`w-[22px] h-[22px] rounded-full cursor-pointer hover:ring-[5px] hover:ring-gray-2 text-white flex items-center justify-center ${getSelectedClass(
                props.byLayerColor!,
                true
              )} ${props.disabled ? 'cursor-not-allowed' : ''}`}
              style={{
                background: props.byLayerColor!,
                ['background-color']: props.byLayerColor!,
              }}
              onClick={() => {
                if (!props.disabled) {
                  props.onByLayerSelect?.();
                }
              }}
              data-testid="bylayer-color-option"
              title="ByLayer"
            >
              <Show when={shouldSelectColor(props.byLayerColor!, true)}>
                <modus-wc-icon size="sm" name="check_bold" />
              </Show>
            </div>
          </Show>
          <For each={defaultColorsFlat}>
            {(item) => (
              <div
                class={`w-[22px] h-[22px] rounded-full cursor-pointer hover:ring-[5px] hover:ring-gray-2 text-white flex items-center justify-center ${getSelectedClass(
                  item,
                  false
                )} ${props.disabled ? 'cursor-not-allowed' : ''}`}
                style={{ background: item }}
                onClick={() => {
                  if (!props.disabled) {
                    handleColorChange(tinycolor(item).toRgb());
                    props.onSave?.(tinycolor(item).toRgb());
                  }
                }}
                title={getClosestNamedColor(item)}
              >
                <Show when={shouldSelectColor(item, false)}>
                  <modus-wc-icon size="sm" name="check_bold" />
                </Show>
              </div>
            )}
          </For>
        </div>
        <label class="text-xs text-gray-6">
          {t('shared.ui.colorPicker.customColorLabel')}
        </label>
        <div
          data-testid="custom-palette-container"
          class="grid grid-cols-6 auto-rows-min grid-flow-row gap-[11px]"
        >
          <For each={allCustomColors()}>
            {(item) => (
              <div
                class={`w-[22px] h-[22px] rounded-full cursor-pointer hover:ring-[5px] hover:ring-gray-2 text-white flex items-center justify-center ${getSelectedClass(
                  tinycolor(item).toHex(),
                  false
                )} ${props.disabled ? 'cursor-not-allowed' : ''}`}
                style={{ background: tinycolor(item).toHexString() }}
                onClick={() => {
                  if (!props.disabled) {
                    handleColorChange(item);
                    if (props.onSave) {
                      props.onSave(item);
                    }
                  }
                }}
                title={getClosestNamedColor(tinycolor(item).toHexString())}
              >
                <Show when={shouldSelectColor(tinycolor(item).toHex(), false)}>
                  <modus-wc-icon size="sm" name="check_bold" />
                </Show>
              </div>
            )}
          </For>
          <button
            data-testid="add-custom-color-button"
            class={`w-[22px] h-[22px] rounded-full flex flex-col items-center align-middle ${
              props.disabled ? 'cursor-not-allowed opacity-95' : ''
            }`}
            onClick={() => {
              if (!props.disabled) {
                setShowColorSelector(true);
              }
            }}
            disabled={props.disabled}
          >
            <modus-wc-icon size="md" name="add_circle" />
          </button>
        </div>
      </Show>
      <Show when={showColorSelector()}>
        <div data-testid="saturation-container">
          <Saturation
            hsv={hsvState()}
            onChange={(satVal) => {
              // only update S and V, keep prev H
              if (!props.disabled) {
                handleColorChange(undefined, {
                  h: hsvState().h,
                  s: satVal.s,
                  v: satVal.v,
                });
              }
            }}
            height={100}
          />
        </div>
        <div
          data-testid="hue-container"
          class="flex flex-row align-middle w-full h-6"
        >
          <div data-testid="hue-picker" class="h-5 mt-1 mb-1">
            <HuePicker
              width="200px"
              height="12px"
              color={{ h: hsvState().h, s: 1.0, v: 0.5 }} // only need H, S and V remain neutral
              onChange={(color) => {
                // only update H, keep prev S and V
                if (!props.disabled) {
                  handleColorChange(undefined, {
                    h: color.hsv.h,
                    s: hsvState().s,
                    v: hsvState().v,
                  });
                }
              }}
              pointer={() => <div style={customSliderPointerStyle} />}
            />
          </div>
          <div class="grow relative" />
          <div
            class="w-6 h-6 rounded-full align-middle relative right-0"
            style={{
              background: tinycolor(rgbState()).toHexString(),
            }}
          />
        </div>
        <div
          data-testid="rgb-input-container"
          class="flex flex-row relative w-60 h-15 gap-1"
        >
          <ColorInputField
            testId="hex-input"
            inputType="hex"
            labelTranslationKey="shared.ui.colorPicker.hexValueLabel"
            initialValue={tinycolor(rgbState()).toHex()}
            onChange={(val) => {
              if (!props.disabled) {
                handleColorChange(tinycolor(val).toRgb());
              }
            }}
          />
          <ColorInputField
            testId="r-input"
            inputType="rgb"
            labelTranslationKey="shared.ui.colorPicker.redChannelLabel"
            initialValue={rgbState().r.toString()}
            onChange={(val) => {
              onEditableRChange(val);
            }}
          />
          <ColorInputField
            testId="g-input"
            inputType="rgb"
            labelTranslationKey="shared.ui.colorPicker.greenChannelLabel"
            initialValue={rgbState().g.toString()}
            onChange={(val) => {
              onEditableGChange(val);
            }}
          />
          <ColorInputField
            testId="b-input"
            inputType="rgb"
            labelTranslationKey="shared.ui.colorPicker.blueChannelLabel"
            initialValue={rgbState().b.toString()}
            onChange={(val) => {
              onEditableBChange(val);
            }}
          />
        </div>
        <div class="flex relative w-full h-15 gap-2 flex-row-reverse">
          <modus-wc-button
            onClick={onCustomColorCreated}
            disabled={props.disabled}
          >
            {t('shared.ui.colorPicker.saveColorButton')}
          </modus-wc-button>
          <modus-wc-button
            onClick={() => {
              if (!props.disabled) {
                setShowColorSelector(false);
              }
            }}
            color="secondary"
            disabled={props.disabled}
          >
            {t('shared.ui.colorPicker.cancelColorButton')}
          </modus-wc-button>
        </div>
      </Show>
      <Show when={props.useTransparency}>
        <label class="text-xs text-gray-6">
          {t('shared.ui.colorPicker.transparencySliderLabel')}
        </label>
        <div
          class="w-full"
          style={{ ['--range-shdw']: 'var(--modus-wc-color-primary)' }}
        >
          <modus-wc-slider
            data-testid="transparency-slider"
            class="gap-2 w-full h-6 min-h-6"
            min={transparencySliderRange.min}
            max={transparencySliderRange.max}
            step={transparencySliderRange.step}
            value={Math.round(
              (1 - transparency()) *
                (transparencySliderRange.max - transparencySliderRange.min) +
                transparencySliderRange.min
            )}
            size="sm"
            disabled={props.disabled}
            on:inputChange={(ev: ModusWcSliderCustomEvent<InputEvent>) => {
              onTransparencyChange(
                (ev.target.children[0] as HTMLInputElement).value
              );
            }}
          />
        </div>
      </Show>
      <Show when={props.showResetButton}>
        <modus-wc-button
          data-testid="reset-button"
          size="md"
          attr:full-width={true}
          color="tertiary"
          onClick={() => props.onReset?.()}
          disabled={props.disabled}
        >
          {t('shared.ui.colorPicker.resetColorButton')}
        </modus-wc-button>
      </Show>
    </div>
  );
};

export default ColorPickerPanel;
